import React from 'react';
import { Box } from '@mui/material';
import { Button, Typography, List, ListItem } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConnectionUnavailableIllustration, { ConnectionUnavailableVariant } from './ConnectionUnavailableIllustration';
import NetworkScanGateReason, { NetworkScanGateReasonProps } from '../NetworkScanGateReason';

interface Props {
    variant: ConnectionUnavailableVariant;
    assetLocationId: string;
    /** Human-readable labels of the missing required fields (variant "missingData"). */
    missingFields?: string[];
    /** Title of the IoT system the asset belongs to (variant "partOfIotSystem"). */
    systemName?: string;
    /** Refetch handler shown as a retry button (variant "loadFailed"). */
    onRetry?: () => void;
    /** Effective scan and tree target (variant "networkScan"). */
    networkScan?: NetworkScanGateReasonProps;
}

/**
 * Shown on the /connect route instead of the IoT connection wizard when one of
 * the pre-checks fails: the product is not configured for IoT, required
 * asset/customer data is missing, or the form definition could not be loaded.
 */
const ConnectionUnavailableView = ({ variant, assetLocationId, missingFields = [], systemName, onRetry, networkScan }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const texts: Record<ConnectionUnavailableVariant, { headline: string; body: string }> = {
        notAllowed: {
            headline: t('IoT connection not available'),
            body: t('This product is not configured for IoT connection. Please contact the product management team or a global administrator to enable IoT for this product.'),
        },
        missingData: {
            headline: t('Required data is missing'),
            body: t('Some required data is missing to create an IoT connection. Please complete the following information first:'),
        },
        loadFailed: {
            headline: t('Could not load the form'),
            body: t('The connection form could not be loaded. Please try again.'),
        },
        partOfIotSystem: {
            headline: t('IoT connection not available'),
            body: t('You cannot set up an IoT connection for this asset because the asset is part of the IoT system {{systemName}}.', { systemName }),
        },
        // Network scan gate (IBM-1039): ORANGE, RED or not documented blocks a new attach.
        networkScan: {
            headline: t('Network scan required'),
            body: t('An IoT connection can only be set up once a passed network scan is documented for this asset or one of its parent elements.'),
        },
    };

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 3,
                pb: 6,
            }}
        >
            <ConnectionUnavailableIllustration width={220} variant={variant} />

            <Typography level="title-lg" sx={{ pt: 3 }}>
                {texts[variant].headline}
            </Typography>

            <Typography level="body-md" sx={{ pt: 1, maxWidth: 520, color: 'text.secondary' }}>
                {texts[variant].body}
            </Typography>

            {variant === 'missingData' && missingFields.length > 0 && (
                <List
                    marker="disc"
                    size="sm"
                    sx={{
                        pt: 1,
                        flexGrow: 0,
                        '--ListItem-minHeight': '28px',
                        color: 'text.secondary',
                    }}
                >
                    {missingFields.map((field) => (
                        <ListItem key={field}>{field}</ListItem>
                    ))}
                </List>
            )}

            {variant === 'networkScan' && (
                <Box sx={{ pt: 3 }}>
                    <NetworkScanGateReason {...networkScan} align="center" />
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, pt: 4 }}>
                <Button
                    variant="outlined"
                    color="neutral"
                    onClick={() => navigate(`/assets/${assetLocationId}/dashboard`)}
                >
                    {t('Back to dashboard')}
                </Button>
                {variant === 'loadFailed' && onRetry && (
                    <Button variant="solid" onClick={onRetry}>
                        {t('Retry')}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ConnectionUnavailableView;
